import { type Moment } from "moment";

import { BTSelectItem } from "types/apiResponse/apiResponse";
import {
    type AccountingIntegrationType,
    type PaymentBrands,
    type PaymentMethodType,
    type PaymentStatus,
} from "types/enum";

import { type IModalConfiguration } from "commonComponents/btWrappers/BTModal/BTModal";
import {
    AddressEntity,
    type IHasAddress,
} from "commonComponents/entity/address/Address/Address.api.types";
import type CheckoutExample from "commonComponents/financial/Checkout/Checkout.api.json";
import { type IAccountingIdExtraData } from "commonComponents/financial/PaymentMethodDetails/PaymentMethodDetails.api.types";
import {
    type IPaymentProcessingService,
    type IThreeDsResult,
} from "commonComponents/financial/PaymentService/paymentServiceContext/PaymentServiceJs.types";

import {
    type PaymentMethodDto,
    PaymentServiceAccountConfigResponse,
} from "entity/onlinePayment/OnlinePaymentSettings/OnlinePaymentSettings.api.types";

export enum PaymentSuccessTypes {
    Success = 1,
    Failed = 2,
    AwaitingVerification = 3,
    PartialSuccess = 4,
    UnderSubReview = 5,
    AuthenticationRequired = 6,
    AutoProcessed = 7,
}

export interface IPayOnlinePaymentInfo {
    invoiceAmount: number;
    amountPaid: number;
    balance: number;
    fee: number;
    merchantName: string;
    recipientEmails: string[];
    recipientPhone: string;
    merchantId: number; // SubId, or builderId. For distinguishing between merchants beyond name in case of dupe name.
    tax: IPayOnlineTaxInfo | null;
    creditCardRate: number;
    invoiceTitle?: string;
    memo?: string;
    defaultJobPaymentMethodId: number | null;
}

export enum PaymentAmountTypes {
    BalanceDue = 1,
    OtherAmount = 2,
}

export enum CheckoutErrorTypes {
    ContactSupport = 0,
    TryAnotherCard = 1,
    VerifyInformation = 2,
    InsufficientFundsOrCredit = 3,
    WithdrawalLimitExceeded = 4,
    FraudSecurityDenied = 5,
    ExistingDisbursement = 6,
    InvalidBankAccount = 7,
}

export interface ICheckoutFormValues {
    paymentAmountType: PaymentAmountTypes;
    paymentAmount: number | undefined;
    feeAmount: number;
    paymentMethodType: PaymentMethodType | undefined;
    selectedPaymentMethodId: number | string | null | undefined; // string if one-time method, number if saved.
    paymentMethods: PaymentMethod[];
}

export interface ICheckoutProps {
    paymentService?: IPaymentProcessingService;
    customInvoiceId?: string;
    invoiceJobPrefixes?: string;
    supportedPaymentMethods: ISupportedPaymentMethod[];
    paymentInfo: ICheckoutPaymentInfo;
    allowMicrodepositWorkflow: boolean;
    canUseStoredPaymentMethods: boolean;
    canEditPaymentAmount: boolean;
    passCreditCardFeesToOwner: boolean;
    passAchFeesToClient: boolean;
    builderLogoUrl?: string;
    builderCountry: "US" | "CA";
    onSubmitPayment: (
        values: ICheckoutFormValues,
        paymentMethod: PaymentMethodDto,
        userBrowserDetails?: string,
        sharedToken?: string,
    ) => Promise<void>;
    onsubmitMobileWalletPayment?: (
        values: ICheckoutFormValues,
        paymentMethod: PaymentMethodDto,
        userBrowserDetails?: string,
    ) => Promise<void>;
    paymentResult?: PayOnlineResponse;
    handleThreeDsResponse?: (event: IThreeDsResult) => Promise<void>;
    modalConfig?: IModalConfiguration;
    width?: number;
    jobId: number | null;
    externalBuilderId?: string;
    shareToken?: string;
    onPaymentMethodChange?: () => void;
    printLink?: string;
    invoiceDueDate?: Moment;
    typeLabel: string;
    paymentError?: CheckoutErrorTypes;
    errorPaymentMethodId?: string;
    isFromMobileApp?: boolean;
    // only true when coming from mobile app and in version 10.18.0 or greater
    isMobileInAppBrowser?: boolean;
    proposalId?: number | string;
    redirectDetails?: IPaymentMethodRedirectDetails;
    onPlaidIframeClose?: () => void;
    ownerActivationLink?: string;
    labelId?: string;
}

export interface ISupportedPaymentMethod {
    type: PaymentMethodType;
    min: number;
}

interface ICheckoutPaymentInfo {
    invoiceAmount: number;
    amountPaid: number;
    balance: number;
    fee: number;
    merchantName: string;
    recipientEmails: string[];
    merchantId: number; // SubId, or builderId. For distinguishing between merchants beyond name in case of dupe name.
    tax: IPayOnlineTaxInfo | null;
    creditCardRate: number;
    achRate: number;
    invoiceTitle?: string;
    memo?: string;
}

export interface IPayOnlineTaxInfo {
    amount: number;
    inclusive: boolean;
}

export class PaymentMethodsResponse {
    constructor(data: typeof CheckoutExample) {
        this.defaultPaymentMethodType = data.defaultPaymentMethodType;
        this.paymentMethods = data.methods.map((x) => new PaymentMethod(x));
    }
    defaultPaymentMethodType?: PaymentMethodType;
    paymentMethods: PaymentMethod[];
}

// This interface exists for the constructor below. Type Inference is funky with the JSON examples of merchantPaymentMethodId and processorId being nullable
interface IPaymentMethod {
    merchantPaymentMethodId: number | null;
    payerName: string;
    paymentBrand: PaymentBrands;
    paymentType: PaymentMethodType;
    lastFour: string;
    processorPaymentMethodId: string | null;
    payerEmail: string | null;
    payerPhoneNumber: string | null;
    needsMicrodepositVerification: boolean;
    hasMicrodepositsSent: boolean;
    isDefault: boolean;
    institutionName?: string;
    routingNumber?: string;
    isExpired?: boolean;
    feePercent: number;
    flatFee: number;
    nickname?: string | null;
}

export class PaymentMethod {
    constructor(data: IPaymentMethod) {
        this.merchantPaymentMethodId = data.merchantPaymentMethodId;
        this.payerName = data.payerName;
        this.paymentBrand = data.paymentBrand;
        this.paymentType = data.paymentType;
        this.lastFour = data.lastFour;
        this.processorId = data.processorPaymentMethodId;
        this.payerEmail = data.payerEmail;
        this.payerPhoneNumber = data.payerPhoneNumber;
        this.needsMicrodepositVerification = data.needsMicrodepositVerification;
        this.hasMicrodepositsSent = data.hasMicrodepositsSent;
        this.isDefault = data.isDefault;
        this.institutionName = data.institutionName;
        this.routingNumber = data.routingNumber;
        this.isExpired = data.isExpired ?? false;
        this.feePercent = data.feePercent;
        this.flatFee = data.flatFee;
        this.nickname = data.nickname ?? null;
    }

    merchantPaymentMethodId: number | null;
    payerName?: string;
    paymentBrand: PaymentBrands;
    paymentType: PaymentMethodType;
    lastFour: string;
    processorId: string | null;
    payerEmail: string | null;
    payerPhoneNumber: string | null;
    needsMicrodepositVerification: boolean;
    hasMicrodepositsSent: boolean;
    isDefault: boolean;
    institutionName?: string;
    routingNumber?: string;
    isExpired: boolean;
    feePercent: number;
    flatFee: number;
    nickname?: string | null;
}

export class PayOnlineEntity {
    constructor(data: typeof CheckoutExample.payOnlineEntity) {
        this.canEditPaymentAmount = data.canEditPaymentAmount;
        this.canPayWithCreditCard = data.canPayWithCreditCard;
        this.canPayWithECheck = data.canPayWithECheck;
        this.canUseStoredPaymentMethods = data.canUseStoredPaymentMethods;
        this.builderCountry = data.builderCountry as "US" | "CA";
        this.builderLogoUrl = data.builderLogoUrl;
        this.paymentInfo = data.paymentInfo;
        this.allowedCheckRange = data.allowedCheckRange
            ? new AllowedPaymentRange(data.allowedCheckRange)
            : null;
        this.allowedCreditCardRange = data.allowedCreditCardRange
            ? new AllowedPaymentRange(data.allowedCreditCardRange)
            : null;
        this.canUseMicrodepositVerification = data.canUseMicrodepositVerification ?? false;
        this.passCreditCardFeesToOwner = data.passCreditCardFeesToOwner;
        this.passAchFeesToClient = data.passAchFeesToClient;
    }

    canPayWithCreditCard: boolean;
    canPayWithECheck: boolean;
    canUseStoredPaymentMethods: boolean;
    canEditPaymentAmount: boolean;
    builderCountry: "US" | "CA";
    builderLogoUrl?: string;
    paymentInfo: ICheckoutPaymentInfo;
    allowedCheckRange: AllowedPaymentRange | null;
    allowedCreditCardRange: AllowedPaymentRange | null;
    canUseMicrodepositVerification: boolean;
    passCreditCardFeesToOwner: boolean;
    passAchFeesToClient: boolean;
}

class AllowedPaymentRange {
    constructor(data: { minimum: number; maximum: number | null }) {
        this.minimum = data.minimum;
        this.maximum = data.maximum;
    }

    minimum: number;
    maximum: number | null;
}

export class PayOnlineResponse {
    constructor(data: typeof CheckoutExample.payOnlineResponse) {
        this.successType = data.successType;
        this.paymentAmount = data.paymentAmount;
        this.paymentMethodName = data.paymentMethodName;
        this.statementName = data.statementName;
        this.payerEmail = data.payerEmail;
        this.notifiedNames = data.notifiedNames;
        this.checkoutError = data.checkoutError;
        this.threeDsIframeAuthToken = data.threeDsIframeAuthToken ?? null;
        this.splitDetails =
            data.splitDetails === null ||
            data.splitDetails === undefined ||
            data.splitDetails.length === 0
                ? null
                : data.splitDetails.map((x) => new PayOnlineResponseSplitDetails(x));
    }
    successType: PaymentSuccessTypes;
    paymentAmount: number;
    paymentMethodName: string;
    statementName: string;
    payerEmail: string;
    notifiedNames: string[];
    checkoutError: CheckoutErrorTypes | null;
    threeDsIframeAuthToken: string | null;
    splitDetails: PayOnlineResponseSplitDetails[] | null;
}

export class PayOnlineResponseSplitDetails {
    constructor(data: (typeof CheckoutExample.payOnlineResponse.splitDetails)[0]) {
        this.description = data.description;
        this.amount = data.amount;
        this.status = data.status;
    }
    description: string;
    amount: number;
    status: PaymentStatus;
}

export class ThreeDsResult {
    constructor(data: { successType: PaymentSuccessTypes }) {
        this.successType = data.successType;
    }
    successType: PaymentSuccessTypes;
}

export class AccountingPaymentMethodResponse {
    constructor(data: typeof CheckoutExample.accountingPaymentMethodResponse) {
        this.accountingPaymentMethodAccounts = data.accountingPaymentMethodAccounts
            ? data.accountingPaymentMethodAccounts.map(
                  (x) => new BTSelectItem<IAccountingIdExtraData>(x),
              )
            : undefined;
        this.accountingIntegrationType = data.accountingIntegrationType;
        this.accountingPaymentMethodMessage = data.accountingPaymentMethodMessage;
    }

    accountingPaymentMethodAccounts?: BTSelectItem<IAccountingIdExtraData>[];
    accountingIntegrationType: AccountingIntegrationType;
    accountingPaymentMethodMessage?: string;
}

export class NewPaymentServiceMethodData implements IHasAddress {
    constructor(data: typeof CheckoutExample.newPaymentServiceMethodResponse) {
        this.paymentServiceConfig = new PaymentServiceAccountConfigResponse(
            data.paymentServiceConfig,
        );
        this.address = data.address && new AddressEntity(data.address);
        this.mobileWalletFeePercent = data.mobileWalletFeePercent;
        this.mobileWalletFlatFee = data.mobileWalletFlatFee;
    }
    paymentServiceConfig: PaymentServiceAccountConfigResponse;
    address: AddressEntity;
    mobileWalletFeePercent: number;
    mobileWalletFlatFee: number;
}

export class TokenError {
    constructor(data: IPPSTokenError) {
        this.errorCode = data.errorCode;
        this.success = data.success;
    }
    errorCode: number;
    success: boolean;
}

export interface IPPSTokenError {
    errorCode: number;
    success: boolean;
}

export enum TokenErrorCode {
    None = 0,
    Unknown = 1,
    Unauthorized = 2,
}

export interface IPaymentMethodRedirectDetails {
    paymentAmountType: PaymentAmountTypes;
    otherPaymentAmount: number | undefined;
    paymentType: PaymentMethodType;
    accountHolderName?: string;
    zipCode?: string;
    email?: string;
    saveForFuture?: boolean;
    setAsPreferredPaymentMethod?: boolean;
    oauthRedirectLandingUrl?: string;
    phoneNumber?: string;
}

export interface IBankAccountFormValues {
    saveForFuture: boolean;
    accountHolderName: string;
    zipCode: string;
    emailAddress: string;
    setAsPreferred?: boolean;
    accountingAccount?: number;
    accountingId?: IAccountingIdExtraData;
    phoneNumber: string;
}
